import React, { useState } from 'react'
import { useMutation } from '@apollo/client'
import { Modal, Button, Input } from '@plusplusminus/plusplusdash'
import { PencilIcon } from '@heroicons/react/solid'
import { useForm } from 'react-hook-form'
import toast from 'react-hot-toast'
import { UPDATE_ORDER_CUSTOMER } from '../../graphql/mutations/updateOrderCustomer'

interface CustomerFormValues {
  firstName: string
  lastName: string
  email: string
  mobile: string
}

interface EditCustomerDetailsProps {
  customer: {
    id: string
    firstName?: string
    lastName?: string
    email?: string
    mobile?: string
  }
  onSuccess: () => void
}

const EditCustomerDetails: React.FC<EditCustomerDetailsProps> = ({ customer, onSuccess }) => {
  const [isOpen, setIsOpen] = useState(false)
  const {
    register,
    handleSubmit,
    formState: { errors }
  } = useForm<CustomerFormValues>({
    defaultValues: {
      firstName: customer.firstName || '',
      lastName: customer.lastName || '',
      email: customer.email || '',
      mobile: customer.mobile || ''
    }
  })

  const [updateOrderCustomer, { loading }] = useMutation(UPDATE_ORDER_CUSTOMER, {
    onCompleted: () => {
      setIsOpen(false)
      onSuccess()
      toast.success('Customer details updated successfully')
    },
    onError: (error) => {
      console.error('Error updating customer details:', error)
      toast.error('Failed to update customer details')
    }
  })

  const onSubmit = (data: CustomerFormValues) => {
    updateOrderCustomer({
      variables: {
        input: {
          id: customer.id,
          update: {
            firstName: data.firstName,
            lastName: data.lastName,
            email: data.email,
            mobile: data.mobile
          }
        }
      }
    })
  }

  return (
    <>
      <Button variant="plain" onClick={() => setIsOpen(true)} className="text-gray-500 hover:text-gray-700">
        <PencilIcon className="h-4 w-4 mr-1" />
        Edit
      </Button>

      <Modal isOpen={isOpen} onClose={() => setIsOpen(false)} size="sm">
        <Modal.Title>Edit Customer Details</Modal.Title>
        <Modal.Description>
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-4 mt-4">
            <div>
              <label htmlFor="firstName" className="block text-sm font-medium text-gray-700">
                First Name
              </label>
              <Input
                id="firstName"
                variant="standard"
                name="firstName"
                ref={register({ required: 'First name is required' })}
                className={errors.firstName ? 'border-red-500' : ''}
              />
              {errors.firstName && <p className="mt-1 text-xs text-red-500">{errors.firstName.message}</p>}
            </div>
            <div>
              <label htmlFor="lastName" className="block text-sm font-medium text-gray-700">
                Last Name
              </label>
              <Input
                id="lastName"
                variant="standard"
                name="lastName"
                ref={register({ required: 'Last name is required' })}
                className={errors.lastName ? 'border-red-500' : ''}
              />
              {errors.lastName && <p className="mt-1 text-xs text-red-500">{errors.lastName.message}</p>}
            </div>

            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                Email
              </label>
              <Input
                id="email"
                variant="standard"
                type="email"
                name="email"
                ref={register({
                  required: 'Email is required',
                  pattern: {
                    value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                    message: 'Invalid email address'
                  }
                })}
                className={errors.email ? 'border-red-500' : ''}
              />
              {errors.email && <p className="mt-1 text-xs text-red-500">{errors.email.message}</p>}
            </div>
            <div>
              <label htmlFor="mobile" className="block text-sm font-medium text-gray-700">
                Mobile
              </label>
              <Input id="mobile" variant="standard" ref={register({})} name="mobile" />
            </div>

            <div className="flex justify-end space-x-3 mt-6">
              <Button type="button" variant="plain" onClick={() => setIsOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" variant="primary" disabled={loading}>
                {loading ? 'Saving...' : 'Save Changes'}
              </Button>
            </div>
          </form>
        </Modal.Description>
      </Modal>
    </>
  )
}

export default EditCustomerDetails
